import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  FiMail,
  FiPhone,
  FiBriefcase,
  FiAward,
  FiBookOpen,
  FiArrowRight,
  FiClock,
} from "react-icons/fi";
import Header from "../Home/Header";
import Footers from "../Home/Footers";
import AxiosInstance from "../Home/AxiosInstance";
import "../Home/publicPages.css";
import "../Company/EmployerDetails.css";

const INITIAL_COLORS = [
  "#5b5fff",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
];

const getInitialColor = (name) => {
  const code = (name || "A").charCodeAt(0) || 65;
  return INITIAL_COLORS[code % INITIAL_COLORS.length];
};

const getPhotoUrl = (path) => {
  if (!path) return null;
  const cleaned = String(path).replace(/\\/g, "/");
  if (cleaned.startsWith("http")) return cleaned;
  return `${AxiosInstance.defaults.baseURL}/media/${cleaned}`;
};

export default function DetailsOfTrainer() {
  const { trainerId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [educations, setEducations] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [courses, setCourses] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const tryFetch = async (url) => {
      try {
        const res = await AxiosInstance.get(url);
        return res.data;
      } catch (err) {
        if (err.response?.status === 404) return [];
        console.warn(`Error fetching ${url}:`, err.message);
        return [];
      }
    };

    const isTrainerBatch = (batch, id) => {
      const tid = Number(id);
      const batchTrainerId = Number(
        batch.trainer?.trainer_id ?? batch.trainer ?? 0
      );
      const materialTrainerId = Number(
        batch.course_material?.trainer?.trainer_id ??
          batch.course_material?.trainer ??
          0
      );
      return batchTrainerId === tid || materialTrainerId === tid;
    };

    const fetchTrainer = async () => {
      setLoading(true);
      setError("");
      try {
        const profileRes = await AxiosInstance.get(
          `companies/trainer-details/${trainerId}`
        );
        setProfile(profileRes.data);

        const [
          educationRes,
          certRes,
          achieveRes,
          expRes,
          courseRes,
          socialRes,
          batchesRes,
        ] = await Promise.all([
          tryFetch(`companies/trainer/educations/${trainerId}`),
          tryFetch(`companies/trainer/certifications/${trainerId}`),
          tryFetch(`companies/trainer/achievements/${trainerId}`),
          tryFetch(`companies/trainer/work-experiences/${trainerId}`),
          tryFetch(`companies/trainer/courses/${trainerId}/`),
          tryFetch(`companies/trainer/social_link/${trainerId}`),
          tryFetch(`companies/batches/`),
        ]);

        setEducations(Array.isArray(educationRes) ? educationRes : []);
        setCertifications(Array.isArray(certRes) ? certRes : []);
        setAchievements(Array.isArray(achieveRes) ? achieveRes : []);
        setExperiences(Array.isArray(expRes) ? expRes : []);
        setSocialLinks(Array.isArray(socialRes) ? socialRes : []);

        const batches = Array.isArray(batchesRes) ? batchesRes : [];
        const offeredBatches = batches.filter(
          (batch) =>
            batch.is_published === "Yes" && isTrainerBatch(batch, trainerId)
        );

        if (offeredBatches.length > 0) {
          setCourses(offeredBatches);
        } else {
          // Fallback: course materials owned by this trainer (no published batch yet)
          const materials = Array.isArray(courseRes) ? courseRes : [];
          setCourses(materials);
        }
      } catch (err) {
        console.error("Failed to load trainer:", err);
        setError(
          err.response?.status === 404
            ? "Trainer not found."
            : "Failed to load trainer details."
        );
      } finally {
        setLoading(false);
      }
    };

    if (trainerId) fetchTrainer();
  }, [trainerId]);

  if (loading) {
    return (
      <div className="TrainerPage min-vh-100 d-flex flex-column">
        <Header />
        <p className="emp-loading">Loading trainer…</p>
        <Footers />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="TrainerPage min-vh-100 d-flex flex-column">
        <Header />
        <div className="emp-loading">
          <p className="text-danger">{error || "Trainer not found."}</p>
          <nav className="als-breadcrumb als-breadcrumb-light" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="als-breadcrumb-sep" aria-hidden="true">
              ›
            </span>
            <Link to="/trainers">Trainers</Link>
          </nav>
        </div>
        <Footers />
      </div>
    );
  }

  const fullName = profile.alias_name?.trim()
    ? profile.alias_name
    : `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
      "Trainer";
  const photoUrl = getPhotoUrl(profile.image_path);
  const accent = getInitialColor(fullName);
  const roleLabel =
    experiences[0]?.role?.role ||
    experiences[0]?.company_name ||
    null;

  return (
    <div className="TrainerPage min-vh-100 d-flex flex-column">
      <Header />

      <section className="emp-hero">
        <div className="emp-hero-inner">
          <div className="emp-hero-copy">
            <nav className="als-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span className="als-breadcrumb-sep" aria-hidden="true">
                ›
              </span>
              <Link to="/trainers">Trainers</Link>
              <span className="als-breadcrumb-sep" aria-hidden="true">
                ›
              </span>
              <span aria-current="page">{fullName}</span>
            </nav>
            <div className="emp-badges">
              <span className="emp-badge type">Trainer</span>
              {roleLabel && (
                <span className="emp-badge mode">{roleLabel}</span>
              )}
            </div>
            <h1>{fullName}</h1>
            {profile.email && (
              <p className="emp-hero-email">{profile.email}</p>
            )}
            <div className="emp-meta">
              {profile.mobile_no && (
                <div className="emp-meta-item">
                  <FiPhone /> {profile.mobile_no}
                </div>
              )}
              {courses.length > 0 && (
                <div className="emp-meta-item">
                  <FiBookOpen /> {courses.length} course
                  {courses.length === 1 ? "" : "s"}
                </div>
              )}
              {experiences.length > 0 && (
                <div className="emp-meta-item">
                  <FiBriefcase /> {experiences.length} role
                  {experiences.length === 1 ? "" : "s"}
                </div>
              )}
            </div>
          </div>
          <div className="emp-hero-media">
            <div
              className="emp-hero-logo"
              style={
                !photoUrl
                  ? { background: `${accent}1a`, color: accent }
                  : undefined
              }
            >
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt=""
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                fullName.charAt(0).toUpperCase()
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="emp-body flex-grow-1">
        <div className="emp-layout">
          <main className="emp-main">
            <section className="emp-card">
              <h2>About</h2>
              <p className="emp-prose">
                {profile.about ||
                  `${fullName} is a trainer on Open Learnings.`}
              </p>
              {(courses.length > 0 ||
                experiences.length > 0 ||
                certifications.length > 0) && (
                <div className="emp-highlights">
                  {courses.length > 0 && (
                    <span>
                      {courses.length} course{courses.length === 1 ? "" : "s"}
                    </span>
                  )}
                  {experiences.length > 0 && (
                    <span>
                      {experiences.length} experience
                      {experiences.length === 1 ? "" : "s"}
                    </span>
                  )}
                  {certifications.length > 0 && (
                    <span>
                      {certifications.length} certification
                      {certifications.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              )}
            </section>

            <section className="emp-card">
              <h2>Courses offered</h2>
              {courses.length > 0 ? (
                <ul className="emp-internship-list">
                  {courses.map((item) => {
                    const isBatch = Boolean(item.batch_id);
                    const courseName =
                      item.course_material?.course?.course_name ||
                      item.course?.course_name ||
                      item.course_name ||
                      item.batch_name ||
                      "Unnamed Course";
                    const description = isBatch
                      ? [item.mode, item.duration].filter(Boolean).join(" · ") ||
                        item.course_material?.description ||
                        "Details available on course page"
                      : item.description || "Course details available on catalog";
                    const materialId =
                      item.course_material?.course_material_id ||
                      item.course_material_id;

                    return (
                      <li key={item.batch_id || item.course_material_id}>
                        <div>
                          <strong>{courseName}</strong>
                          <p>{description}</p>
                          {isBatch && item.batch_starts_at && (
                            <p>
                              <FiClock
                                style={{
                                  display: "inline",
                                  marginRight: 4,
                                  verticalAlign: "middle",
                                }}
                              />
                              Starts{" "}
                              {new Date(item.batch_starts_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          className="emp-view-role"
                          onClick={() => {
                            if (materialId) {
                              localStorage.setItem(
                                "course_material_id",
                                materialId
                              );
                            }
                            if (isBatch) {
                              navigate(`/courses/${item.batch_id}`);
                            } else {
                              navigate("/courses");
                            }
                          }}
                        >
                          View <FiArrowRight />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="emp-prose">
                  No published courses listed for this trainer yet.
                </p>
              )}
            </section>

            {experiences.length > 0 && (
              <section className="emp-card">
                <h2>Work experience</h2>
                <ul className="emp-list">
                  {experiences.map((exp) => (
                    <li key={exp.experience_id}>
                      <FiBriefcase className="emp-list-icon" />
                      <div>
                        <strong>
                          {exp.role?.role || "Role"} ·{" "}
                          {exp.company_name || "Company"}
                        </strong>
                        <p>{exp.experience || "Experience details"}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {educations.length > 0 && (
              <section className="emp-card">
                <h2>Education</h2>
                <ul className="emp-list">
                  {educations.map((edu) => (
                    <li key={edu.user_education_id}>
                      <FiBookOpen className="emp-list-icon" />
                      <div>
                        <strong>
                          {edu.education?.qualification || "Qualification"}
                          {edu.specialization
                            ? ` · ${edu.specialization}`
                            : ""}
                        </strong>
                        <p>{edu.college_name || "Institution"}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {certifications.length > 0 && (
              <section className="emp-card">
                <h2>Certifications</h2>
                <ul className="emp-list">
                  {certifications.map((cert) => (
                    <li key={cert.user_certification_id}>
                      <FiAward className="emp-list-icon" />
                      <div>
                        <strong>
                          {cert.certification_name || "Certification"}
                        </strong>
                        <p>{cert.issue_authority || "Issuing authority"}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {achievements.length > 0 && (
              <section className="emp-card">
                <h2>Achievements</h2>
                <ul className="emp-list">
                  {achievements.map((ach) => (
                    <li key={ach.achievement_id}>
                      <FiAward className="emp-list-icon" />
                      <div>
                        <strong>{ach.title || "Achievement"}</strong>
                        {ach.description && <p>{ach.description}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </main>

          <aside className="emp-sidebar">
            <section className="emp-card emp-contact-card">
              <h2>Contact</h2>
              <ul className="emp-contact-list">
                {profile.email && (
                  <li>
                    <FiMail />
                    <a href={`mailto:${profile.email}`}>{profile.email}</a>
                  </li>
                )}
                {profile.mobile_no && (
                  <li>
                    <FiPhone />
                    <a href={`tel:${profile.mobile_no}`}>{profile.mobile_no}</a>
                  </li>
                )}
                {socialLinks.map((link) => (
                  <li key={link.social_link_id}>
                    <FiBriefcase />
                    <a href={link.url} target="_blank" rel="noreferrer">
                      {link.platform || "Social link"}
                    </a>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="emp-cta"
                onClick={() => navigate("/courses")}
              >
                Browse courses
              </button>
            </section>
          </aside>
        </div>
      </div>

      <Footers />
    </div>
  );
}
